import React, { useEffect, useRef, useState } from 'react';

export interface DateRangeStrings {
  selectPeriod: string;
  from: string;
  to: string;
  thisMonth: string;
  lastMonth: string;
  thisQuarter: string;
  lastQuarter: string;
  thisYear: string;
  lastYear: string;
}

export interface DateRangePickerProps {
  label: string;
  startValue: string;
  endValue: string;
  activePreset: string;
  strings: DateRangeStrings;
  disabled?: boolean;
  onChange: (preset: string, start: string, end: string) => void;
}

export const DATE_RANGE_PRESETS = [
  'thisMonth',
  'lastMonth',
  'thisQuarter',
  'lastQuarter',
  'thisYear',
  'lastYear'
] as const;

export type DateRangePreset = typeof DATE_RANGE_PRESETS[number];

function toIso(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
}

function endOfMonth(year: number, monthIndex: number): Date {
  // monthIndex: 0-based. Day 0 of next month = last day of this month.
  return new Date(year, monthIndex + 1, 0);
}

export function computeRange(preset: string, today: Date): { start: string; end: string } | null {
  const y = today.getFullYear();
  const m = today.getMonth();
  switch (preset) {
    case 'thisMonth':
      return { start: toIso(new Date(y, m, 1)), end: toIso(endOfMonth(y, m)) };
    case 'lastMonth':
      return { start: toIso(new Date(y, m - 1, 1)), end: toIso(endOfMonth(y, m - 1)) };
    case 'thisQuarter': {
      const qStart = Math.floor(m / 3) * 3;
      return { start: toIso(new Date(y, qStart, 1)), end: toIso(endOfMonth(y, qStart + 2)) };
    }
    case 'lastQuarter': {
      const qStart = Math.floor(m / 3) * 3 - 3;
      return { start: toIso(new Date(y, qStart, 1)), end: toIso(endOfMonth(y, qStart + 2)) };
    }
    case 'thisYear':
      return { start: toIso(new Date(y, 0, 1)), end: toIso(new Date(y, 11, 31)) };
    case 'lastYear':
      return { start: toIso(new Date(y - 1, 0, 1)), end: toIso(new Date(y - 1, 11, 31)) };
    default:
      return null;
  }
}

function presetLabel(id: string, strings: DateRangeStrings): string {
  switch (id) {
    case 'thisMonth': return strings.thisMonth;
    case 'lastMonth': return strings.lastMonth;
    case 'thisQuarter': return strings.thisQuarter;
    case 'lastQuarter': return strings.lastQuarter;
    case 'thisYear': return strings.thisYear;
    case 'lastYear': return strings.lastYear;
    default: return '';
  }
}

function formatShortDate(iso: string): string {
  if (!iso) return '';
  const d = new Date(iso + 'T00:00:00');
  return d.toLocaleDateString('nl-NL', { day: 'numeric', month: 'short', year: 'numeric' });
}

export const DateRangePicker: React.FC<DateRangePickerProps> = ({
  label, startValue, endValue, activePreset, strings, disabled, onChange
}) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const pickPreset = (id: string) => {
    const r = computeRange(id, new Date());
    if (!r) return;
    onChange(id, r.start, r.end);
  };

  const changeStart = (v: string) => onChange('', v, endValue);
  const changeEnd = (v: string) => onChange('', startValue, v);

  const triggerText = activePreset && presetLabel(activePreset, strings)
    ? `${presetLabel(activePreset, strings)} · ${formatShortDate(startValue)} – ${formatShortDate(endValue)}`
    : startValue && endValue
      ? `${formatShortDate(startValue)} – ${formatShortDate(endValue)}`
      : strings.selectPeriod;

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <label className="form-label" style={{ fontSize: '12px', marginBottom: '2px', display: 'block' }}>{label}</label>
      <button
        type="button"
        className="btn btn-sm btn-outline-primary"
        disabled={disabled}
        onClick={() => setOpen(o => !o)}
        style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', textAlign: 'left', minWidth: '260px', justifyContent: 'flex-start' }}
      >
        <span aria-hidden="true">📅</span>
        <span style={{ flex: 1 }}>{triggerText}</span>
        <span style={{ color: '#6c757d' }} aria-hidden="true">▾</span>
      </button>
      {open && (
        <div
          role="dialog"
          style={{
            position: 'absolute', zIndex: 1000, top: 'calc(100% + 4px)', left: 0,
            width: 340, background: 'white', border: '1px solid #dee2e6',
            borderRadius: 6, boxShadow: '0 4px 12px rgba(0,0,0,0.08)', padding: 14
          }}
        >
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 4, marginBottom: 12 }}>
            {DATE_RANGE_PRESETS.map(id => (
              <button
                key={id}
                type="button"
                onClick={() => pickPreset(id)}
                style={{
                  padding: '6px 10px', borderRadius: 4, fontSize: 12.5, textAlign: 'left',
                  background: activePreset === id ? 'var(--bs-primary)' : 'transparent',
                  color: activePreset === id ? 'white' : '#212529',
                  fontWeight: activePreset === id ? 500 : 400,
                  border: 'none', cursor: 'pointer'
                }}
              >
                {presetLabel(id, strings)}
              </button>
            ))}
          </div>
          <div style={{ borderTop: '1px solid #e9ecef', paddingTop: 12, display: 'flex', gap: 8 }}>
            <div style={{ flex: 1 }}>
              <label style={{ display: 'block', fontSize: 11, color: '#6c757d', marginBottom: 3, textTransform: 'uppercase', letterSpacing: '.3px' }}>
                {strings.from}
              </label>
              <input
                type="date"
                className="form-control form-control-sm"
                value={startValue}
                onChange={e => changeStart(e.target.value)}
                disabled={disabled}
              />
            </div>
            <div style={{ flex: 1 }}>
              <label style={{ display: 'block', fontSize: 11, color: '#6c757d', marginBottom: 3, textTransform: 'uppercase', letterSpacing: '.3px' }}>
                {strings.to}
              </label>
              <input
                type="date"
                className="form-control form-control-sm"
                value={endValue}
                onChange={e => changeEnd(e.target.value)}
                disabled={disabled}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
